// Carrier times (ADR 0055, v1's rule): what a carrier's page writes —
// `YYYY-MM-DD` or `YYYY-MM-DDTHH:MM`, the port's local time — is shown AS
// WRITTEN, never converted to Europe/Rome. The API sends them without a zone;
// anything after the minutes (seconds, `Z`, an offset) is ignored, so an older
// answer that stored the local hour «as if UTC» still shows the written hour.
// Plancia's own instants (read times, positions, revisions, Plancia's ETA) are
// real instants and keep the usual local formatting.

const CARRIER_TIME = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/;

/**
 * A carrier time as a `Date` whose local fields are the written ones (so
 * `dayMonth`, `dayMonthTime`, `clockTime` print it unchanged). A day without
 * time is local midnight. `null` for no value or text that is not a date.
 */
export function carrierDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const m = CARRIER_TIME.exec(value.trim());
  if (!m) return null;
  const [, y, mo, d, h, mi] = m;
  const date = new Date(Number(y), Number(mo) - 1, Number(d), h ? Number(h) : 0, mi ? Number(mi) : 0);
  return Number.isFinite(date.getTime()) ? date : null;
}

/** `carrierDate` in milliseconds (`NaN` when not a date), to order and compare. */
export function carrierMs(value: string | null | undefined): number {
  return carrierDate(value)?.getTime() ?? Number.NaN;
}

/** Whether the carrier gave only a day (no hour, or a round midnight). */
export function carrierDayOnly(value: string | null | undefined): boolean {
  const m = value ? CARRIER_TIME.exec(value.trim()) : null;
  return !!m && (!m[4] || (m[4] === '00' && m[5] === '00'));
}

const ZONED = /(?:Z|[+-]\d{2}:?\d{2})$/;

/**
 * For helpers that print both kinds: a value with a zone (`Z`, `+01:00`) is a
 * Plancia instant and is shown in local time; a value without one is a
 * carrier time and is shown as written.
 */
export function anyDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  if (ZONED.test(value.trim())) {
    const date = new Date(value);
    return Number.isFinite(date.getTime()) ? date : null;
  }
  return carrierDate(value);
}

/** `anyDate` in milliseconds (`NaN` when not a date). */
export function anyMs(value: string | null | undefined): number {
  return anyDate(value)?.getTime() ?? Number.NaN;
}
